'use client'
import { ContainerCustom, Icon } from '@/components'
import { CALLBACK_URL_KEY } from '@/constants'
import { AddToCartButton } from '@/features/products/components'
import ColorOptionItem from '@/features/products/components/product-detail/select-color'
import { SelectQuantity } from '@/features/products/components/product-detail/select-quantity'
import { useProductDetailV2State } from '@/features/products/contexts/product-detail'
import { useSizeGuideContext } from '@/features/products/contexts/side-guide-context'
import { useAffiliateLevelContext } from '@/features/ref/contexts/aff-level-context'
import { useAccountStore } from '@/store'
import { useVoucherStore } from '@/store/voucher'
import { Box, Button, Divider, Stack, StackProps, styled, Typography, useTheme } from '@mui/material'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useQueryState } from 'nuqs'
import { Fragment, useEffect, useMemo } from 'react'
import { DiscountText } from './discount-text'
import ImageMobile from './image-mobile'
import ListButtonLinks from './list-button-links'
import { ListComboProduct } from './list-combo-product'

type Props = {
  data: any
  wrapBtnProps?: Omit<StackProps, 'children'>
  wrapContentProps?: Omit<StackProps, 'children'>
  hasContainer?: boolean
  showItem?: {
    discount?: boolean
    combo?: boolean
  }
  wrapListContentProps?: Omit<StackProps, 'children'>
  wrapActionBtnProps?: Omit<StackProps, 'children'>
  onClickScrollReviews?: () => void
  hidePriceSale?: boolean
}

export const StickyBarBottom = ({
  data,
  wrapBtnProps,
  hasContainer = true,
  showItem = { combo: true, discount: true },
  wrapContentProps,
  wrapListContentProps,
  wrapActionBtnProps,
  onClickScrollReviews,
  hidePriceSale = false,
}: Props) => {
  const { isAuthenticated } = useAccountStore()
  const { handleOpenSideGuide, recommendedSizes } = useSizeGuideContext()
  const { handleOpen, tempQuantityWithCoupon, selectedOptions, selectedVariant, productSelected } =
    useProductDetailV2State()
  const { dataCartPriceRule } = useVoucherStore()
  const pathname = usePathname()
  const { data: affiliateBonus } = useAffiliateLevelContext()
  const t = useTranslations()
  const { productAddToCart, quantity } = useProductDetailV2State()
  const isShowTextDiscount = useMemo(() => {
    try {
      if (!dataCartPriceRule) return null
      if (!Object.keys(dataCartPriceRule).length) return null
      return Boolean(dataCartPriceRule?.single_price)
    } catch (error) {
      return null
    }
  }, [dataCartPriceRule])
  // const { width } = useWindowSize()
  // const isSmallScreen = width < 1024
  const handleShowPopupSelectOption = () => {
    handleOpen()
  }
  const Component = hasContainer ? ContainerCustom : Box

  return (
    <Fragment>
      <Box
        sx={{
          backgroundColor: 'white',
          zIndex: 100,
          width: '100%',
        }}
      >
        <Component component={'div'}>
          <Stack
            justifyContent={'space-between'}
            alignItems={'stretch'}
            gap={'8px'}
            {...wrapContentProps}
            sx={{
              lg: { borderTop: '1px solid #BBBBBA' },
              paddingY: 1,
              ...wrapContentProps?.sx,
            }}
          >
            <Stack gap={2} {...wrapListContentProps}>
              <ImageMobile
                src={
                  selectedVariant?.product?.image.url ||
                  productSelected?.product?.image.url ||
                  data?.image.url
                }
                title={data?.name}
                data={data}
                onClickScrollReviews={onClickScrollReviews}
              />

              <Stack spacing={1} flexDirection={'column'} className="list-options-desktop">
                <ListOptions />
              </Stack>
              <SelectQuantity hidePriceSale={hidePriceSale} />
              {showItem?.discount && showItem?.combo && (
                <>
                  {isShowTextDiscount && !!tempQuantityWithCoupon?.length && (
                    <Stack
                      className="discount-item"
                      gap={{ xs: 0, lg: 0.5 }}
                      direction={'row'}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <DiscountText
                        props={{
                          sx: {
                            fontSize: 'clamp(0.75rem, 0.568rem + 0.284vw, 0.875rem)',
                          },
                        }}
                        showPreText={true}
                        showIcon={false}
                        finalOption={{ hide: true, text: 'ĐÂY LÀ COMBO HỜI NHẤT CHO ANH EM!' }}
                      />
                    </Stack>
                  )}
                  {showItem?.combo && <ListComboProduct props={{ sx: { mt: { xs: '4px', lg: '0px' } } }} />}
                </>
              )}
            </Stack>

            <Stack spacing={1} {...wrapActionBtnProps}>
              <Divider />
              {!isAuthenticated && (
                <Link
                  href={`/register?${CALLBACK_URL_KEY}=${encodeURIComponent(pathname)}`}
                  style={{
                    display: 'block',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      p: '.6em ',
                      borderRadius: '8px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      color: '#fff8f2',
                      textTransform: 'uppercase',
                      width: '100%',
                      fontSize: {
                        xs: '12px',
                        md: '16px',
                      },
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      isolation: 'isolate',
                      letterSpacing: '0.1px',
                      background: 'linear-gradient(90deg, #ff512f, #f09819)',

                      '&:hover': {
                        transform: 'scale(1.02)',
                      },

                      '@keyframes glowBorder': {
                        '0%': { backgroundPosition: '0% 50%' },
                        '50%': { backgroundPosition: '100% 50%' },
                        '100%': { backgroundPosition: '0% 50%' },
                      },
                    }}
                  >
                    <Box>
                      <Box
                        component="span"
                        sx={{
                          zIndex: 2,
                          fontSize: '1.1em',
                          display: 'inline-block',
                          animation: 'pulseIcon 1.8s ease-in-out infinite',
                          '@keyframes pulseIcon': {
                            '0%, 100%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.2)' },
                          },
                        }}
                      >
                        🎉
                      </Box>{' '}
                      <Box component="span">Đăng ký tài khoản để tham gia chương trình</Box>{' '}
                      <Box
                        component="span"
                        sx={{
                          zIndex: 2,
                          fontSize: '1.1em',
                          display: 'inline-block',
                          animation: 'pulseIcon 1.8s ease-in-out infinite',
                          '@keyframes pulseIcon': {
                            '0%, 100%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.2)' },
                          },
                        }}
                      >
                        🎉
                      </Box>{' '}
                    </Box>

                    <Box
                      component="span"
                      sx={{
                        zIndex: 2,
                        position: 'relative',
                        textAlign: 'center',
                        background: 'linear-gradient(90deg, #fff, #fff5c0, #ffffff, #fff5c0, #fff)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundSize: '200% 100%',
                        animation: 'shineText 3s infinite linear',
                        '@keyframes shineText': {
                          '0%': { backgroundPosition: '200% 0' },
                          '100%': { backgroundPosition: '-200% 0' },
                        },
                      }}
                    >
                      membership giảm giá lên tới {affiliateBonus?.slice(-1)?.[0].bonus || 5}%
                    </Box>
                  </Box>
                </Link>
              )}
              <Stack
                gap={2}
                direction={'row'}
                sx={{
                  flexDirection: {
                    xs: 'column',
                    md: 'row',
                  },
                }}
              >
                <Stack width={'100%'} gap={1}>
                  <Stack spacing={1} direction={'row'} flex={1}>
                    <AddToCartButton
                      variant="outlined"
                      quantity={quantity}
                      productSelected={productAddToCart}
                    />
                    <AddToCartButton
                      variant="contained"
                      quantity={quantity}
                      title="Thanh toán ngay"
                      productSelected={productAddToCart}
                      isBuyNow
                    />
                  </Stack>
                  <ListButtonLinks />
                </Stack>
              </Stack>

              {/* <Stack direction={'row'} spacing={1} {...wrapBtnProps}>
                <CommonButton
                  onClick={handleShowPopupSelectOption}
                  // LinkComponent={Link}
                  // href={SOCIAL_LINKS.facebook}
                  // target="_blank"
                  // rel="noopener noreferrer"
                  sx={{
                    flex: 1,
                    width: '100%',
                    gap: '4px',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: { xs: '0.875rem', md: '1.125rem' },
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                  }}
                  className="main-btn"
                >
                  <Icon.Talk sx={{ color: 'inherit', fontSize: { xs: '16px', md: '1.2rem' } }} />
                  <Typography
                    sx={{
                      color: 'inherit',
                      fontSize: 'inherit',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      alignmentBaseline: 'middle',
                      lineHeight: { xs: 1.3, md: 1.5 },
                    }}
                  >
                    Chọn size, màu và chốt deal ngay
                  
                  </Typography>
                  <Icon.ArrowRight sx={{ color: 'inherit', fontSize: '1.2rem', lineHeight: 1 }} />
                </CommonButton>
                <CommonButton
                component={Link}
                target="_blank"
                rel="noopener noreferrer"
                href="/blog"
                sx={{
                  flexDirection: 'column',
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                }}
              >
                <Icon.Talk sx={{ color: 'inherit', fontSize: { xs: '16px', md: 'inherit' } }} />
                <Typography sx={{ color: 'inherit', fontSize: 'inherit' }}>{t('Tìm hiểu ưu đãi')}</Typography>
              </CommonButton>
              </Stack> */}
            </Stack>
          </Stack>
        </Component>
      </Box>
    </Fragment>
  )
}
const SizeButton = styled(Button, {
  shouldForwardProp: (prop) => !['isSelected', 'isAvailable'].includes(prop as string),
})<{
  isSelected?: boolean
  isAvailable?: boolean
}>(({ theme, isSelected, isAvailable }) => ({
  borderRadius: '8px',
  backgroundColor: !isAvailable ? '#F0F0F0' : isSelected ? theme.palette.primary.main : 'transparent',
  color: !isAvailable ? '#BBBBBA' : isSelected ? 'white' : 'black',
  border: '1px solid',
  borderColor: !isAvailable ? '#F0F0F0' : isSelected ? theme.palette.primary.main : '#BBBBBA',
  pointerEvents: isAvailable ? 'auto' : 'none',
  opacity: isAvailable ? 1 : 0.5,
  transition: 'all 0.2s',
  cursor: isAvailable ? 'pointer' : 'not-allowed',
  minWidth: 0,
  width: '48px',
  height: '36px',
  [theme.breakpoints.down('md')]: {
    padding: '2px 8px',
    width: '46px',
    height: '36px',
  },
}))
type TOptionProps = {
  defaultSelectedOptions?: Record<string, string>
  sx?: React.CSSProperties
  changeImageOnSelect?: boolean
}
//#region list options
export const ListOptions = ({ defaultSelectedOptions, sx, changeImageOnSelect = true }: TOptionProps) => {
  const { handleOpenSideGuide, recommendedSizes } = useSizeGuideContext()
  useSizeGuideContext()
  const [color, setColor] = useQueryState('color')

  const { getAvailableOptions, handleOptionChange, selectedOptions, sortedOptions } =
    useProductDetailV2State()

  useEffect(() => {
    if (defaultSelectedOptions) {
      handleOptionChange(defaultSelectedOptions?.attribute_code, defaultSelectedOptions?.uid)
    }
  }, [defaultSelectedOptions, handleOptionChange])

  const theme = useTheme()
  const t = useTranslations()

  const getSelectedColorLabel = () => {
    const colorOption = sortedOptions.find((opt) => opt.attribute_code === 'color')
    if (!colorOption) return ''

    const selectedColorValue = colorOption.values?.find((value) => value.uid === selectedOptions?.['color'])

    return selectedColorValue?.label || ''
  }
  const getSelectedSizeLabel = () => {
    const sizeOption = sortedOptions.find((opt) => opt.attribute_code === 'size')
    if (!sizeOption) return ''
    const selectedSizeValue = sizeOption.values?.find((value) => value.uid === selectedOptions?.['size'])
    return selectedSizeValue?.label || ''
  }

  const selectedColorLabel = getSelectedColorLabel()
  const selectedSizeLabel = getSelectedSizeLabel()
  return (
    <Stack sx={sx} gap={1}>
      {sortedOptions.map((option, index) => {
        const availableOptions = getAvailableOptions(option.attribute_code)
        const isColor = option.attribute_code === 'color'
        const isSize = option.attribute_code === 'size'
        if (availableOptions.length === 0) return null
        return (
          <Stack spacing={{ xs: 0.5, md: 1 }} key={option?.uid || index}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
              <Stack sx={{ fontSize: '14px' }} alignItems={'center'} direction={'row'} spacing={1}>
                {isColor ? (
                  <Icon.Color sx={{ fontSize: 'inherit' }} />
                ) : (
                  <Icon.Ruler sx={{ fontSize: 'inherit' }} />
                )}
                <Typography sx={{ fontSize: 'inherit' }} fontWeight={300}>
                  {option?.label}:{' '}
                  <Typography component={'span'}>{isColor ? selectedColorLabel : ''}</Typography>
                  <Typography component={'span'}>{isSize ? selectedSizeLabel : ''}</Typography>
                </Typography>
                {isSize && recommendedSizes?.[0] && (
                  <Typography sx={{ fontSize: 'inherit' }} fontWeight={600}>
                    Đề xuất size {recommendedSizes?.[0]}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Stack direction={'row'} gap={1} flexWrap={'wrap'} sx={{ position: 'relative' }}>
              {availableOptions?.map((value) => {
                const isSelected = selectedOptions?.[option?.attribute_code] === value?.uid
                const isDisabled = !value?.hasStock

                if (option.attribute_code === 'color') {
                  return (
                    <ColorOptionItem
                      key={value.uid}
                      value={value}
                      isSelected={isSelected}
                      isDisabled={isDisabled}
                      trigger="click"
                      onAction={(uid) => {
                        handleOptionChange(option.attribute_code, uid)
                        if (changeImageOnSelect) {
                          setColor(uid)
                        }
                      }}
                    />
                  )
                }

                // Render text option
                return (
                  <Box sx={{ position: 'relative' }} key={value?.uid}>
                    {isSelected && (
                      <Box
                        // layoutId="size-border"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          border: `2px solid ${theme.palette.primary.main}`,
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: '8px',
                          pointerEvents: 'none',
                          zIndex: 0,
                          transition: 'all 0.2s ease-in-out',
                        }}
                        // initial={false}
                        // transition={{
                        //   type: 'spring',
                        //   stiffness: 400,
                        //   damping: 30,
                        // }}
                      />
                    )}
                    <SizeButton
                      isSelected={isSelected}
                      isAvailable={!isDisabled}
                      onClick={() => !isDisabled && handleOptionChange(option?.attribute_code, value?.uid)}
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <Typography sx={{ color: 'inherit', fontSize: 'inherit' }} fontWeight={600}>
                        {value?.label}
                      </Typography>
                    </SizeButton>
                  </Box>
                )
              })}
            </Stack>
            {isSize && (
              <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={1}
                onClick={() => handleOpenSideGuide()}
                sx={{ cursor: 'pointer', '&:hover': { '.icon': { transform: 'translateX(2px)' } } }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {t('Footer.bottom.size-guide')}
                </Typography>
                <Icon.ArrowRight className="icon" sx={{ fontSize: 16, transition: 'all 0.2s ease' }} />
              </Stack>
            )}
          </Stack>
        )
      })}
    </Stack>
  )
}
